import React, { useState, useRef, useEffect } from 'react';
import Styles from './patchStyle.module.scss';
import { FaCalendarAlt } from 'react-icons/fa';
import Project from '../../common/Utils/project';
import { useAuth } from 'oidc-react';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS, Role } from '../../common/Utils/permissions';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { createPortal } from 'react-dom';
import api from '../services/apiService';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { useRemediationPlanService } from '../services/useRemediationPlanService';

const PatchCard = ({ task, getRemediationList }) => {
  const progressColor = task.progress < 50 ? 'bg-red-500' : 'bg-green-500';

  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const buttonRef = useRef(null);
  const { updateRemediationPlanStatus } = useRemediationPlanService();

  const role = auth.userData?.profile?.role ?? 'tenantadmin';
  const perms = PERMISSIONS[role as Role] || {};
  const { addToast } = useToast();

  const handleOptionClick = (status) => {
    setShowDropdown(false);
    handleStatusChange(task.id, status);
  };

  const handleStatusChange = async (id, status) => {
    try {
      if (auth && auth.userData) {
        const tenantId = selectedTenantId;
        await updateRemediationPlanStatus(tenantId, id, status);
        addToast('Plan updated successfully', 'success', 4000);
        await getRemediationList();
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      addToast('Failed to update plan', 'error', 4000);
    }
  };
  const dropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showDropdown &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Portal Dropdown component
  const DropdownMenu = ({ anchorRef, visible, onSelect }) => {
    const [pos, setPos] = useState({ top: 0, left: 0 });

    useEffect(() => {
      if (anchorRef.current && visible) {
        const rect = anchorRef.current.getBoundingClientRect();
        setPos({
          top: rect.bottom + window.scrollY + 6,
          left: rect.right - 140 + window.scrollX, // Adjust width if needed
        });
      }
    }, [anchorRef, visible]);

    if (!visible) return null;
    return createPortal(
      <div
        style={{
          position: 'absolute',
          zIndex: 9999,
          top: pos.top,
          left: pos.left,
          backgroundColor: '#2c2c2c',
          color: '#ccc',
          borderRadius: 8,
          padding: 8,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',
          width: '132px',
        }}
      >
        <button onClick={() => onSelect('Approved')} className="block w-full text-left px-4 py-2 hover:bg-[#1e1e1e] rounded">
          Approve
        </button>
        <button onClick={() => onSelect('ApprovalRejected')} className="block w-full text-left px-4 py-2 hover:bg-[#1e1e1e] rounded">
          Reject
        </button>
      </div>,
      document.body,
    );
  };

  return (
    <div className={`${Styles.card} p-4 rounded-lg shadow-lg bg-[#1e1e1e] text-white w-full max-w-sm relative`}>
      {/* First Row: Owner, Title, Date */}
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 flex items-center justify-center rounded-full bg-[#2a2a2a] text-white text-xs font-semibold border border-gray-500"
            title={task.prediction ? 'Emergency Remediation Plan' : 'Normal Remediation Plan'}
          >
            {task.prediction ? 'E' : 'N'}
          </div>
          <span className="text-gray-300 text-sm font-medium truncate w-32">{task.title}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <FaCalendarAlt />
          <span>{task.date}</span>
        </div>
      </div>

      {/* Divider */}
      <div className="w-full border-t border-gray-600 my-3 mb-5"></div>

      {/* Progress + Endpoints */}
      <div className="flex items-center justify-between mt-3">
        <div className="w-full max-w-[70%] relative">
          <div className="w-full bg-gray-600 h-2 rounded-full relative">
            <div className={`h-2 ${progressColor} rounded-full`} style={{ width: `${task.progress}%` }}></div>
            <div
              className="w-3 h-3 bg-white rounded-full absolute top-1/2 -translate-y-1/2"
              style={{ left: `calc(${task.progress}% - 6px)` }}
            >
              <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-xs text-gray-300">{task.progress}%</span>
            </div>
          </div>
        </div>
        <div className="flex items-center text-sm ml-4">
          <span className="font-bold text-lg text-white">{String(task.endpoints).padStart(2, '0')}</span>
          <span className="ml-1 text-gray-400">Endpoints</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center mt-4">
        <button
          className={`${Styles.button} px-6 py-2 rounded-full text-sm flex-1`}
          onClick={() => navigate(`/patchcenter/RemediationReport/${task.id}`)}
        >
          {/* {task.progress < 100 ? 'View' : 'Completed'} */}
          View
        </button>

        {task.originalStatus === 'Created' && perms.remediationPlan === 'create' && (
          <div>
            <button
              ref={buttonRef}
              className="text-xl ml-2 p-2 rounded-[30px]"
              onClick={() => setShowDropdown(!showDropdown)}
              style={{
                background: 'linear-gradient(0deg, #29292B 0%, #29292B 100%), linear-gradient(90deg, #A50811 0%, #161617 100%), #FFF',
                borderRadius: '30px',
              }}
            >
              ⋮
            </button>
            {/* Portal Dropdown */}
            <DropdownMenu anchorRef={buttonRef} visible={showDropdown} onSelect={handleOptionClick} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PatchCard;
